import React, { useState } from 'react';
import { backendEndpoints } from "../../libs/backendEndpoints";
import OrderableTable from "../layout/OrderableTable";
import {ColumnDef} from "@tanstack/react-table";
import { Button } from "fearless-common-ui";
import { Modal } from "react-bootstrap";
import { Pages } from "../layout";
import Description from "../Description";

/**
 * Convert the PHP comment format into a nicer format.
 * @param comment
 */
function format(comment: string) {
  const lines = comment
    // Strip the initial /**
    .replace(/\/\*\*\n \* /, '')
    // Strip the trailing '*/
    .replace(/\*\//, '')
    // Keep the lines by splitting on the *
    .split(' * ');
  return lines.map((i, idx) => <>{i}{idx < lines.length - 1 && <br />}</>)
}

/**
 * Button to preview some content in a modal.
 *
 * @param html The Mail HTML code.
 * @param header The modal header
 * @param description A description of the html content.
 * @param mail Mail information, e.g. subject, etc.
 */
function ModalPreviewButton({ html, header, description, mail }: { html: string; header: string; description: string, mail: CommunicationMock['mail'] }) {
  const [show, setShow] = useState<boolean>(false);

  return (
    <>
      <Button size="sm" onClick={() => setShow(true)}>
        Show example
      </Button>
      {show && (
        <Modal
          size="lg"
          show={show}
          dialogClassName="shadow-lg"
          backdrop
          onHide={() => setShow(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {header}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <table className="table table-sm">
              <tbody>
              <tr>
                <td width={200}>Mail subject</td>
                <td>{mail.subject}</td>
              </tr>
              <tr>
                <td>Description</td>
                <td>
                  <Description>{format(description)}</Description>
                </td>
              </tr>
              </tbody>
            </table>
            <iframe className="w-100" height={700} srcDoc={html} title="email-preview" />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

interface CommunicationMock {
  id: number;
  class_name: string;
  description: string;
  example_html: string;
  mail: {
    subject: string;
    greeting: string;
  }
}

const columns: ColumnDef<CommunicationMock>[] = [
  { header: 'Code Class', accessorKey: 'class_name' },
  { header: 'Description', accessorKey: 'description', cell: ({ getValue }) => {
    const value = getValue();
    return <Description className="mb-0">{typeof value === 'string' ? `${format(value.slice(0, 70))}...`: null}</Description>
    } },
  { header: '', accessorKey: 'example_html', cell: ({ row, getValue }) => (
      <ModalPreviewButton
        html={(getValue() as string) || ''}
        header={row.original.class_name}
        mail={row.original.mail}
        description={row.original.description}
      />
    )
  },
]

export default function AdminCommunicationMocks() {
  return (
    <Pages.Centred padded>
      <div className="d-inline-flex align-items-center gap-4">
        <h1>Mail template mocks</h1>
      </div>
      <Description className="neutral-500">
        These are mocks of mail. You cannot edit them here.
      </Description>
      <OrderableTable<CommunicationMock>
        pagination={false}
        baseEndpoint={backendEndpoints.getAdminCommunicationMocks}
        memoisedColumns={columns}
      />
    </Pages.Centred>
  );
}
